import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Stack, Typography, CircularProgress,
} from '@mui/material';
import { httpClient } from '../services/http-client';

interface Choice {
  symbol: string;
  name?: string;
  currency?: string;
  exchange?: string;
}

interface Resolution {
  id: string;
  raw_symbol: string;
  institution?: string;
  chosen: Choice;
  alternatives: Choice[];
  basis: string;
  confidence: number;
  shares: string;
  accounts: string[];
}

/**
 * The basis is stored as a sentence FRAGMENT -- both the resolver and
 * migration 077 write it lowercase so it can be embedded mid-sentence -- and
 * it is rendered here as its own sentence. Capitalising at the point of
 * display keeps every producer free to phrase it either way, rather than
 * requiring each one to guess how it will be shown.
 */
const asSentence = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const label = (c: Choice) =>
  [c.symbol, c.name].filter(Boolean).join(' — ') + (c.exchange ? ` (${c.exchange})` : '');

/**
 * Asks about tickers that name more than one company.
 *
 * WHY THIS EXISTS AT ALL. A bare ticker does not identify a security: "T" is
 * Telus in Toronto and AT&T in New York. The importer picks the best-supported
 * reading and applies it so the import completes, then asks here rather than
 * guessing silently -- nobody reviews a number that was never flagged.
 *
 * DELIBERATELY RARE. Only genuine cross-listings reach this banner. An earlier
 * design flagged every US holding at a Canadian broker and would have shown
 * roughly forty of these at once; alert fatigue teaches people to dismiss the
 * banner, and then they dismiss the one that matters. On real data this
 * renders nothing at all.
 *
 * The position is named in shares and accounts because a prompt that says only
 * "T" is unanswerable -- "310 shares in your TD account" is recognisable.
 */
export const SymbolReviewBanner: React.FC = () => {
  const [items, setItems] = useState<Resolution[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    httpClient
      .get<any>('/investments/symbol-resolutions')
      .then((r) => {
        const d = (r as any)?.data ?? r;
        setItems(d?.resolutions ?? []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const answer = async (id: string, symbol: string) => {
    setBusy(id);
    try {
      await httpClient.post(`/investments/symbol-resolutions/${id}`, { symbol });
      // Reload rather than removing locally: applying an answer rewrites
      // stored rows, so the rest of the page is now stale too.
      window.location.reload();
    } catch {
      setBusy(null);
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      {items.map((r) => (
        <Alert key={r.id} severity="warning" sx={{ mb: 1.5 }}>
          <AlertTitle>
            Is {r.raw_symbol} the right company?
          </AlertTitle>

          <Typography variant="body2" sx={{ mb: 1 }}>
            We recorded it as <strong>{label(r.chosen)}</strong>. {asSentence(r.basis)}.
          </Typography>

          <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {Number(r.shares) > 0 && (
              <Chip size="small" variant="outlined" label={`${Number(r.shares).toLocaleString()} shares`} />
            )}
            {r.accounts?.map((a) => (
              <Chip key={a} size="small" variant="outlined" label={a} />
            ))}
            {r.institution && (
              <Chip size="small" variant="outlined" label={r.institution} />
            )}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              disabled={busy === r.id}
              onClick={() => answer(r.id, r.chosen.symbol)}
            >
              {busy === r.id ? <CircularProgress size={16} /> : `Yes, ${r.chosen.symbol}`}
            </Button>
            {r.alternatives.map((alt) => (
              <Button
                key={alt.symbol}
                size="small"
                variant="outlined"
                disabled={busy === r.id}
                onClick={() => answer(r.id, alt.symbol)}
              >
                No, it&rsquo;s {label(alt)}
              </Button>
            ))}
          </Stack>
        </Alert>
      ))}
    </Box>
  );
};
